import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, CalendarCheck, ArrowRight } from "lucide-react";

const IMG =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/6c0f367cca2954e37cc43df12dabc27ab5ae3d2225313ab53fc49411ef2a492c.png";

const ITEMS = [
  { title: "Seasonal tune-up", note: "Pre-summer cooling check" },
  { title: "Filter reminders", note: "Auto SMS every 60–90 days" },
  { title: "Drain line check", note: "Clear & flush condensate line" },
  { title: "Coil cleaning", note: "Indoor + outdoor coil rinse" },
  { title: "Maintenance membership interest", note: "Capture recurring revenue" },
];

export default function MaintenancePlan() {
  return (
    <section
      data-testid="maintenance-section"
      aria-labelledby="maintenance-heading"
      className="relative bg-mist-soft py-20 sm:py-24"
    >
      <div className="container-page grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-14">
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="lg:col-span-5"
        >
          <div className="relative">
            <div className="overflow-hidden rounded-3xl border border-mist-line bg-white shadow-lift">
              <img
                src={IMG}
                alt="A clean HVAC air filter being swapped, representing seasonal maintenance for an air conditioning system."
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 hidden rotate-[3deg] rounded-2xl border border-aqua/30 bg-white px-3 py-2 text-xs font-medium text-aqua-deep shadow-lift sm:block">
              Capture recurring revenue
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          <span className="pill">
            <CalendarCheck className="h-3 w-3 text-aqua" />
            Maintenance plans
          </span>
          <h2
            id="maintenance-heading"
            className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl lg:text-[2.5rem]"
          >
            Capture recurring maintenance interest.
          </h2>
          <p className="mt-4 max-w-xl text-base text-navy/70 sm:text-lg">
            A clean checklist that lets homeowners express interest in
            ongoing care &mdash; and lets contractors build a predictable book
            of business.
          </p>

          <div className="mt-6 rounded-2xl border border-mist-line bg-white shadow-soft">
            <ul className="divide-y divide-mist-line">
              {ITEMS.map((item) => (
                <li
                  key={item.title}
                  className="flex items-center justify-between gap-4 px-5 py-4"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-aqua/15 text-aqua-deep">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <div>
                      <p className="text-[15px] font-semibold text-navy">
                        {item.title}
                      </p>
                      <p className="text-xs text-navy/55">{item.note}</p>
                    </div>
                  </div>
                  <span className="hidden text-[11px] font-medium uppercase tracking-[0.14em] text-navy/45 sm:inline">
                    Included
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <Link
            to="/request-service?intent=maintenance"
            data-testid="maintenance-cta"
            className="group mt-7 inline-flex items-center gap-2 rounded-full bg-sun px-5 py-3 text-sm font-semibold text-navy-deep shadow-sunGlow transition-transform hover:scale-[1.02]"
          >
            Schedule Maintenance
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
